<template>
  <div class="nfse-modern">
    <NavBar />
    <AuthBySessionExpired />
    <new-nfse @created="getNfse" />
    <view-nfse :nfse="nfse" @updated="getNfse" @close="nfse = null" />

    <div class="nfse-container">
      <!-- Header Section -->
      <div class="nfse-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-invoice-dollar title-icon"></i>
              Notas Fiscais
            </h1>
            <p class="page-subtitle">Emissão e acompanhamento de NFS-e</p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'nfse')"
              class="btn-add-modern"
              @click="$bvModal.show('new_nfse')"
            >
              <i class="fas fa-plus mr-2"></i>
              Emitir nota
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="nfse-content">
        <!-- Filters -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-filter filter-icon"></i>
                Status
              </label>
              <v-select
                v-model="search_status"
                :items="statusOptions"
                placeholder="Todos"
                outlined
                dense
                hide-details
                clearable
                class="modern-input"
                @change="getNfse"
              />
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <div v-show="nfseList.length == 0 && !loading" class="no-results">
                <i class="fas fa-file-invoice no-results-icon"></i>
                <h3 class="no-results-title">Nenhuma nota encontrada</h3>
                <p class="no-results-text">
                  Ajuste o filtro ou emita uma nova nota
                </p>
              </div>

              <v-data-table
                v-show="nfseList.length > 0"
                v-if="permission('read', 'nfse')"
                :headers="headers"
                :items="nfseList"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                class="modern-table"
                :no-data-text="'Nenhuma nota encontrada'"
                :loading-text="'Carregando notas...'"
              >
                <template v-slot:[`item.resident_name`]="{ item }">
                  {{ item.resident_name || "—" }}
                </template>

                <template v-slot:[`item.service_value`]="{ item }">
                  {{ formatCurrency(item.service_value) }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <span class="status-badge" :class="statusClass(item.status)">
                    {{ statusLabel(item.status) }}
                  </span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn class="btn-action-edit" small @click="nfse = item">
                    <i class="fas fa-eye mr-1"></i>
                    Ver
                  </v-btn>
                </template>
              </v-data-table>

              <ErrorsMessages :errors="errors" />
            </b-overlay>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import routes from "../routes";
import api from "../services/api";
import permission from "../lib/permission";
import NewNfse from "../templates/nfse/new-nfse.vue";
import ViewNfse from "../templates/nfse/view-nfse.vue";

export default {
  name: "NfseEmission",
  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    NewNfse,
    ViewNfse,
  },
  data() {
    return {
      search_status: null,
      statusOptions: [
        { text: "Rascunho", value: "draft" },
        { text: "Processando", value: "processing" },
        { text: "Autorizada", value: "authorized" },
        { text: "Rejeitada", value: "rejected" },
        { text: "Cancelada", value: "cancelled" },
      ],
      headers: [
        { text: "DATA", value: "created_at" },
        { text: "RESIDENTE", value: "resident_name" },
        { text: "VALOR", value: "service_value" },
        { text: "STATUS", value: "status" },
        { text: "AÇÕES", value: "actions", sortable: false },
      ],
      nfse: null,
      nfseList: [],
      loading: true,
      errors: [],
    };
  },
  created() {
    this.getNfse();
  },
  methods: {
    permission,

    handleToBack: () => routes.go(-1),

    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("pt-BR");
    },

    statusLabel(status) {
      const found = this.statusOptions.find((s) => s.value === status);
      return found ? found.text : status;
    },

    statusClass(status) {
      const map = {
        draft: "status-draft",
        processing: "status-processing",
        authorized: "status-authorized",
        rejected: "status-rejected",
        cancelled: "status-cancelled",
      };
      return map[status] || "status-draft";
    },

    async getNfse() {
      try {
        this.loading = true;

        const params = {};
        if (this.search_status) params.status = this.search_status;

        const response = await api.get("/rest-home/nfse/paginate", { params });
        const { error, errors, nfse } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.nfseList = nfse;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.nfse-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.nfse-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.nfse-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
}

.nfse-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 280px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 400px;
}

.no-results {
  text-align: center;
  padding: 80px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.modern-table {
  background: transparent !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-draft {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.status-processing {
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  color: #a16207;
  border: 1px solid #facc15;
}

.status-authorized {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-rejected {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.status-cancelled {
  background: #e5e7eb;
  color: #4b5563;
  border: 1px solid #9ca3af;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}
</style>